// IDEVRA sound engine — Web Audio API synthesis (no external assets).
// Boot chime, mech-warrior button clanks, hover ticks, back blips, whooshes.

const STORE_KEY = "idevra_muted";
const THEME_KEY = "idevra_theme_v2";
const VOL_KEY = "idevra_volume";
const WATER_KEY = "idevra_watermix";
const AUTO_KEY = "idevra_garden_auto";

// Web Audio synthesis peaks are conservative — boost all devices, compressor prevents clipping.
const IS_MOBILE =
  typeof navigator !== "undefined" &&
  (navigator.maxTouchPoints > 2 || /android|iphone|ipad|ipod/i.test(navigator.userAgent));
const MASTER_BOOST = IS_MOBILE ? 6 : 3;

class SoundEngine {
  constructor() {
    this.ctx = null;
    this.master = null;
    this.muted =
      typeof window !== "undefined" && localStorage.getItem(STORE_KEY) === "1";
    this.ambient = null;
    this.theme =
      (typeof window !== "undefined" && localStorage.getItem(THEME_KEY)) || "classic";
    if (this.theme === "retro" || this.theme === "orchestra") {
      this.theme = "melody";
      localStorage.setItem(THEME_KEY, "melody");
    }
    const v = typeof window !== "undefined" ? parseFloat(localStorage.getItem(VOL_KEY)) : NaN;
    this.volume = Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.4;
    let wm = {};
    try {
      wm = JSON.parse(localStorage.getItem(WATER_KEY)) || {};
    } catch {
      wm = {};
    }
    this.waterMix = { pour: 1, trickle: 1, plonk: 1, birds: 1, rain: 0, crickets: 0, ...wm };
    this.gardenAuto =
      typeof window !== "undefined" && localStorage.getItem(AUTO_KEY) === "1";
  }

  // Birdsong mornings, zen afternoons, crickets after dark.
  _timeOfDayMix() {
    const h = new Date().getHours();
    if (h >= 5 && h < 11) return { pour: 0.45, trickle: 0.65, plonk: 0.35, birds: 1, rain: 0, crickets: 0 };
    if (h >= 11 && h < 18) return { pour: 1, trickle: 1, plonk: 1, birds: 1, rain: 0, crickets: 0 };
    return { pour: 0.45, trickle: 0.6, plonk: 0.55, birds: 0.15, rain: 0, crickets: 0.85 };
  }

  setGardenAuto(on) {
    this.gardenAuto = on;
    localStorage.setItem(AUTO_KEY, on ? "1" : "0");
    if (on) {
      Object.entries(this._timeOfDayMix()).forEach(([part, v]) => this.setWaterMix(part, v));
    }
  }

  setWaterMix(part, v) {
    this.waterMix[part] = Math.min(1, Math.max(0, v));
    localStorage.setItem(WATER_KEY, JSON.stringify(this.waterMix));
    const w = this.ambient && this.ambient.water;
    if (w && this.ctx) {
      const t = this.ctx.currentTime;
      if (part === "pour") w.pourG.gain.setTargetAtTime(0.055 * this.waterMix.pour, t, 0.05);
      if (part === "rain") w.rainG.gain.setTargetAtTime(0.09 * this.waterMix.rain, t, 0.05);
      if (part === "trickle") w.trickG.gain.setTargetAtTime(this.waterMix.trickle, t, 0.05);
    }
  }

  plonk() {
    if (!this._ready()) return;
    this._plonk(this.ctx.currentTime);
  }

  birds() {
    if (!this._ready() || this.waterMix.birds < 0.02) return;
    this._birdCall(this.ctx.currentTime, true);
  }

  crickets() {
    if (!this._ready() || this.waterMix.crickets < 0.02) return;
    this._cricketTrill(this.ctx.currentTime, Math.max(this.waterMix.crickets, 0.5));
  }

  // Evening cricket — rapid pulsed trill.
  _cricketTrill(t0, k) {
    const f = 4200 + Math.random() * 600;
    const n = 8 + Math.floor(Math.random() * 6);
    for (let i = 0; i < n; i++) {
      const ts = t0 + i * 0.045;
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = "sine";
      o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, ts);
      g.gain.exponentialRampToValueAtTime(0.008 * k, ts + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, ts + 0.04);
      o.connect(g).connect(this.master);
      o.start(ts);
      o.stop(ts + 0.05);
    }
  }

  _chirp(t0, k) {
    if (k < 0.02) return;
    const f0 = 2200 + Math.random() * 1600;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(f0, t0);
    o.frequency.exponentialRampToValueAtTime(f0 * (1.25 + Math.random() * 0.35), t0 + 0.05);
    o.frequency.exponentialRampToValueAtTime(f0 * 0.9, t0 + 0.1);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.02 * k, t0 + 0.015);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.12);
    o.connect(g).connect(this.master);
    o.start(t0);
    o.stop(t0 + 0.15);
  }

  // Japanese garden bird — chirp cluster, sometimes an uguisu-style rising whistle.
  _birdCall(t0, force = false) {
    const k = this.waterMix.birds;
    if (k < 0.02) return;
    const kk = Math.max(k, force ? 0.5 : k);
    if (Math.random() < 0.3) {
      // hooo-kekyo: long rising whistle then two quick chirps
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(1500, t0);
      o.frequency.exponentialRampToValueAtTime(3000, t0 + 0.35);
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.016 * kk, t0 + 0.08);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.4);
      o.connect(g).connect(this.master);
      o.start(t0);
      o.stop(t0 + 0.45);
      this._chirp(t0 + 0.45, kk);
      this._chirp(t0 + 0.62, kk);
    } else {
      const n = 2 + Math.floor(Math.random() * 3);
      for (let i = 0; i < n; i++) this._chirp(t0 + i * (0.12 + Math.random() * 0.08), kk);
    }
  }

  _masterGain() {
    // perceptual curve — linear slider values barely change loudness
    return this.volume * this.volume * MASTER_BOOST;
  }

  setVolume(v) {
    this.volume = Math.min(1, Math.max(0, v));
    localStorage.setItem(VOL_KEY, String(this.volume));
    if (this.master && this.ctx && !this.muted) {
      this.master.gain.setTargetAtTime(this._masterGain(), this.ctx.currentTime, 0.02);
    }
  }

  setTheme(name) {
    this.theme = name;
    localStorage.setItem(THEME_KEY, name);
  }

  unlock() {
    if (!this.ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : this._masterGain();
      const comp = this.ctx.createDynamicsCompressor();
      comp.threshold.value = -20;
      comp.knee.value = 24;
      comp.ratio.value = 8;
      comp.attack.value = 0.003;
      comp.release.value = 0.25;
      this.master.connect(comp).connect(this.ctx.destination);
    }
    if (this.ctx.state === "suspended") this.ctx.resume();
  }

  toggleMute() {
    this.muted = !this.muted;
    localStorage.setItem(STORE_KEY, this.muted ? "1" : "0");
    if (this.master && this.ctx) {
      this.master.gain.setTargetAtTime(this.muted ? 0 : this._masterGain(), this.ctx.currentTime, 0.02);
    }
    return this.muted;
  }

  _osc(type, freq, t0, dur, peak, freqEnd) {
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t0);
    if (freqEnd) o.frequency.exponentialRampToValueAtTime(freqEnd, t0 + dur);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + Math.min(0.015, dur / 4));
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    o.connect(g).connect(this.master);
    o.start(t0);
    o.stop(t0 + dur + 0.05);
  }

  _noise(t0, dur, peak, filterFreq, type = "lowpass") {
    const len = Math.ceil(this.ctx.sampleRate * dur);
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const f = this.ctx.createBiquadFilter();
    f.type = type;
    f.frequency.setValueAtTime(filterFreq, t0);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(peak, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(f).connect(g).connect(this.master);
    src.start(t0);
  }

  _ready() {
    if (!this.ctx || this.muted) return false;
    if (this.ctx.state === "suspended") this.ctx.resume();
    return true;
  }

  // Short focus-move blip (theme aware).
  tick() {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;
    if (this.theme === "retro") {
      this._osc("square", 1046.5, t, 0.035, 0.05);
    } else if (this.theme === "classic") {
      this._osc("sine", 1318.5, t, 0.06, 0.08, 987);
    } else {
      this._osc("triangle", 1900, t, 0.045, 0.09, 1300);
      this._osc("square", 3800, t, 0.012, 0.025);
    }
  }

  // Select press (theme aware).
  clank() {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;
    if (this.theme === "retro") {
      this._osc("square", 440, t, 0.06, 0.09, 220);
      this._osc("square", 880, t + 0.05, 0.08, 0.07);
      this._noise(t, 0.05, 0.08, 3000, "highpass");
    } else if (this.theme === "classic") {
      this._osc("sine", 180, t, 0.14, 0.2, 72);
      this._osc("sine", 1760, t + 0.02, 0.16, 0.05);
      this._noise(t, 0.06, 0.08, 900);
    } else {
      this._noise(t, 0.09, 0.35, 750);
      this._osc("square", 160, t, 0.13, 0.28, 62);
      this._osc("sine", 2400, t + 0.012, 0.03, 0.06);
      this._noise(t + 0.05, 0.12, 0.08, 2600, "highpass");
    }
  }

  // Descending back-out blip (theme aware).
  back() {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;
    if (this.theme === "retro") {
      this._osc("square", 660, t, 0.06, 0.07);
      this._osc("square", 440, t + 0.07, 0.09, 0.07);
    } else if (this.theme === "classic") {
      this._osc("sine", 600, t, 0.14, 0.1, 300);
    } else {
      this._osc("sine", 720, t, 0.11, 0.12, 320);
    }
  }

  // Air-sweep for panel transitions (theme aware).
  whoosh() {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;
    if (this.theme === "retro") {
      this._noise(t, 0.2, 0.09, 900, "bandpass");
    } else if (this.theme === "classic") {
      this._noise(t, 0.42, 0.08, 1100, "bandpass");
    } else {
      this._noise(t, 0.38, 0.12, 1800, "bandpass");
    }
  }

  // Console power-on (theme aware).
  boot() {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;

    if (this.theme === "retro") {
      // 8-bit power-on: low hum ramp + rising chip arpeggio
      this._osc("square", 65.41, t, 1.3, 0.15, 130.81);
      [261.63, 329.63, 392, 523.25, 659.25, 783.99, 1046.5, 1318.5].forEach((f, i) => {
        this._osc("square", f, t + 0.5 + i * 0.11, 0.14, 0.055);
      });
      this._osc("square", 1568, t + 1.5, 0.6, 0.05);
      this._noise(t + 1.5, 0.25, 0.03, 4000, "highpass");
      return;
    }

    if (this.theme === "classic") {
      // Warm, mellow swell — no shimmer
      const sub = this.ctx.createOscillator();
      const sg = this.ctx.createGain();
      sub.type = "sine";
      sub.frequency.setValueAtTime(44, t);
      sub.frequency.linearRampToValueAtTime(55, t + 2.6);
      sg.gain.setValueAtTime(0.0001, t);
      sg.gain.exponentialRampToValueAtTime(0.2, t + 1.3);
      sg.gain.exponentialRampToValueAtTime(0.0001, t + 3.6);
      sub.connect(sg).connect(this.master);
      sub.start(t);
      sub.stop(t + 3.8);
      [196, 261.63, 329.63, 392].forEach((f, i) => {
        const t0 = t + 0.8 + i * 0.22;
        const o = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        o.type = "sine";
        o.frequency.setValueAtTime(f, t0);
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.08, t0 + 0.5);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 2.8);
        o.connect(g).connect(this.master);
        o.start(t0);
        o.stop(t0 + 3);
      });
      return;
    }

    const sub = this.ctx.createOscillator();
    const sg = this.ctx.createGain();
    sub.type = "sine";
    sub.frequency.setValueAtTime(48, t);
    sub.frequency.linearRampToValueAtTime(60, t + 2.2);
    sg.gain.setValueAtTime(0.0001, t);
    sg.gain.exponentialRampToValueAtTime(0.22, t + 1.1);
    sg.gain.exponentialRampToValueAtTime(0.0001, t + 3.2);
    sub.connect(sg).connect(this.master);
    sub.start(t);
    sub.stop(t + 3.4);

    const chord = [220, 330, 440, 554.4, 660];
    chord.forEach((f, i) => {
      const t0 = t + 0.7 + i * 0.16;
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(f, t0);
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.07, t0 + 0.4);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 2.6);
      o.connect(g).connect(this.master);
      o.start(t0);
      o.stop(t0 + 2.8);
    });

    const shimmer = this.ctx.createOscillator();
    const shg = this.ctx.createGain();
    shimmer.type = "sine";
    shimmer.frequency.setValueAtTime(1760, t + 1.4);
    shg.gain.setValueAtTime(0.0001, t + 1.4);
    shg.gain.exponentialRampToValueAtTime(0.028, t + 1.8);
    shg.gain.exponentialRampToValueAtTime(0.0001, t + 3.4);
    shimmer.connect(shg).connect(this.master);
    shimmer.start(t + 1.4);
    shimmer.stop(t + 3.6);
  }

  // PlayStation-launch style chime: deep swell, rising gliss, bright bell shimmer.
  psLaunch() {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;

    if (this.theme === "retro") {
      // Chiptune fanfare version
      [261.63, 329.63, 392, 523.25, 659.25, 783.99, 1046.5].forEach((f, i) => {
        this._osc("square", f, t + i * 0.09, 0.12, 0.06);
      });
      this._osc("square", 1318.5, t + 0.65, 0.5, 0.05);
      this._osc("square", 65.41, t, 0.9, 0.14, 82);
      this._noise(t + 0.65, 0.3, 0.04, 4000, "highpass");
      return;
    }

    // Deep cinematic sub swell (the "boom")
    const sub = this.ctx.createOscillator();
    const sg = this.ctx.createGain();
    sub.type = "sine";
    sub.frequency.setValueAtTime(55, t);
    sub.frequency.linearRampToValueAtTime(41, t + 1.8);
    sg.gain.setValueAtTime(0.0001, t);
    sg.gain.exponentialRampToValueAtTime(0.3, t + 0.35);
    sg.gain.exponentialRampToValueAtTime(0.0001, t + 2.1);
    sub.connect(sg).connect(this.master);
    sub.start(t);
    sub.stop(t + 2.3);

    // Rising glissando sweep (PS1-style shimmer rise)
    const gliss = this.ctx.createOscillator();
    const gg = this.ctx.createGain();
    gliss.type = "sine";
    gliss.frequency.setValueAtTime(320, t + 0.1);
    gliss.frequency.exponentialRampToValueAtTime(2600, t + 1.3);
    gg.gain.setValueAtTime(0.0001, t + 0.1);
    gg.gain.exponentialRampToValueAtTime(0.06, t + 0.5);
    gg.gain.exponentialRampToValueAtTime(0.0001, t + 1.5);
    gliss.connect(gg).connect(this.master);
    gliss.start(t + 0.1);
    gliss.stop(t + 1.6);

    // Warm orchestral-style major chord swell
    [261.63, 329.63, 392, 523.25].forEach((f, i) => {
      const t0 = t + 0.25 + i * 0.05;
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = "triangle";
      o.frequency.setValueAtTime(f, t0);
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.075, t0 + 0.5);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 2.2);
      o.connect(g).connect(this.master);
      o.start(t0);
      o.stop(t0 + 2.4);
    });

    // High bell sparkle on top
    [1567.98, 2093].forEach((f, i) => {
      const t0 = t + 0.9 + i * 0.18;
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(f, t0);
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.045, t0 + 0.06);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.4);
      o.connect(g).connect(this.master);
      o.start(t0);
      o.stop(t0 + 1.6);
    });

    // Soft airy wash underneath
    this._noise(t + 0.15, 1.2, 0.03, 1400, "bandpass");
  }

  // 8-bit ascending arpeggio for the Konami code.
  konami() {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;
    [523.25, 659.25, 783.99, 1046.5, 1318.5, 1568].forEach((f, i) => {
      this._osc("square", f, t + i * 0.07, 0.1, 0.07);
    });
    this._osc("square", 2093, t + 0.45, 0.35, 0.06);
    this._noise(t + 0.45, 0.2, 0.04, 4500, "highpass");
  }

  // Extended 8-bit fanfare synced to the KonamiFX cinematic (~4s).
  konamiCinematic() {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;
    this._noise(t, 0.12, 0.1, 300);
    [523.25, 659.25, 783.99, 1046.5, 1318.5, 1568].forEach((f, i) => {
      this._osc("square", f, t + i * 0.07, 0.1, 0.07);
    });
    this._osc("square", 2093, t + 0.45, 0.3, 0.055);
    for (let i = 0; i < 10; i++) {
      this._osc("square", i % 2 ? 1318.5 : 1046.5, t + 0.6 + i * 0.12, 0.06, 0.045);
    }
    this._noise(t + 2.0, 0.3, 0.14, 220);
    this._osc("square", 65.4, t + 2.0, 0.35, 0.11, 48);
    [783.99, 880, 987.77].forEach((f, i) => this._osc("square", f, t + 2.1 + i * 0.09, 0.1, 0.065));
    [1046.5, 1318.5, 1568, 2093].forEach((f, i) =>
      this._osc(i < 2 ? "square" : "triangle", f, t + 2.42, 1.3, [0.055, 0.05, 0.045, 0.03][i])
    );
    this._noise(t + 2.42, 0.5, 0.04, 4500, "highpass");
    [2637, 3135.96, 3951.07].forEach((f, i) => this._osc("sine", f, t + 2.75 + i * 0.16, 0.35, 0.028));
  }

  // Signature sting per venture — like a game title screen.
  jingle(slug) {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;
    this._noise(t, 0.07, 0.18, 700);
    this._osc("square", 150, t, 0.1, 0.16, 70);
    const J = {
      toothrocket: { w: "triangle", n: [[523.25, 0, 0.09], [659.25, 0.08, 0.09], [783.99, 0.16, 0.09], [1046.5, 0.24, 0.3]] },
      toothmatrix: { w: "sine", n: [[440, 0, 0.14], [554.37, 0.1, 0.14], [659.25, 0.2, 0.14], [880, 0.3, 0.36]] },
      vigilant: { w: "triangle", n: [[739.99, 0, 0.09], [739.99, 0.14, 0.09], [987.77, 0.28, 0.32]] },
      aetria: { w: "sine", n: [[392, 0, 0.55], [587.33, 0.06, 0.55], [783.99, 0.12, 0.6]] },
      qbone: { w: "sine", n: [[1318.5, 0, 0.07], [1760, 0.11, 0.07], [2093, 0.22, 0.28]] },
      dow: { w: "triangle", n: [[523.25, 0, 0.09], [392, 0.09, 0.09], [659.25, 0.18, 0.09], [523.25, 0.27, 0.26]] },
      aeroforge: { w: "sawtooth", n: [[110, 0, 0.35, 0.05], [220, 0.12, 0.3, 0.045], [1567.98, 0.3, 0.24, 0.035]] },
    };
    const j = J[slug];
    if (!j) return;
    const wave = this.theme === "retro" ? "square" : j.w;
    j.n.forEach(([f, off, dur, peak]) => this._osc(wave, f, t + 0.12 + off, dur, peak || 0.07));
  }

  // Ascending PS-style trophy ding.
  trophy() {
    if (!this._ready()) return;
    const t = this.ctx.currentTime;
    if (this.theme === "retro") {
      [659.25, 880, 1046.5, 1318.5].forEach((f, i) => this._osc("square", f, t + i * 0.08, 0.11, 0.06));
      return;
    }
    this._osc("sine", 880, t, 0.15, 0.12);
    this._osc("sine", 1174.66, t + 0.09, 0.35, 0.14);
    this._osc("sine", 1760, t + 0.16, 0.4, 0.05);
    this._noise(t + 0.02, 0.15, 0.035, 5000, "highpass");
  }

  // Generative ambient (theme aware).
  startAmbient() {
    this.unlock();
    if (!this.ctx || this.ambient) return;
    if (this.theme === "melody") return this._ambientMelody();
    if (this.theme === "classic") return this._ambientClassic();
    return this._ambientModern();
  }

  // MUSIC BOX — gentle showcase melody over a warm pad.
  _ambientMelody() {
    const t = this.ctx.currentTime;
    const bus = this.ctx.createGain();
    bus.gain.setValueAtTime(0.0001, t);
    bus.gain.exponentialRampToValueAtTime(0.14, t + 2.5);
    bus.connect(this.master);
    const padOscs = [];
    [[110, "triangle", 0.05], [164.81, "triangle", 0.035], [220.4, "sine", 0.03]].forEach(([f, ty, p]) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = ty;
      o.frequency.value = f;
      g.gain.value = p;
      o.connect(g).connect(bus);
      o.start();
      padOscs.push(o);
    });
    const scale = [440, 493.88, 554.37, 659.25, 739.99, 880, 987.77, 1108.73];
    const phrase = [0, 2, 4, 7, -1, 5, 4, 2, -1, 1, 3, 5, -1, 4, 2, 0, -1, -1];
    let step = 0;
    const note = (f, t0, peak, dur) => {
      [1, 2, 4].forEach((h, i) => {
        const o = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        o.type = "sine";
        o.frequency.value = f * h;
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(peak / (i * 2 + 1), t0 + 0.012);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur / (i + 1));
        o.connect(g).connect(bus);
        o.start(t0);
        o.stop(t0 + dur + 0.05);
      });
    };
    const sparkle = setInterval(() => {
      if (this.muted || !this.ambient) return;
      const idx = phrase[step % phrase.length];
      const t0 = this.ctx.currentTime;
      if (idx >= 0) note(scale[idx], t0, 0.055, 1.7);
      if (step % 9 === 0) note(220, t0, 0.04, 2.4);
      step += 1;
    }, 440);
    this.ambient = { bus, lfo: null, oscs: padOscs, sparkle };
  }

  // MODERN — flowing energy: slow-breathing detuned pad + calm 432Hz-family swells and gliding sweeps.
  _ambientModern() {
    const t = this.ctx.currentTime;
    const bus = this.ctx.createGain();
    bus.gain.setValueAtTime(0.0001, t);
    bus.gain.exponentialRampToValueAtTime(0.045, t + 3.5);
    const filter = this.ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 460;
    filter.connect(bus);
    bus.connect(this.master);
    const lfo = this.ctx.createOscillator();
    const lfoGain = this.ctx.createGain();
    lfo.frequency.value = 0.03;
    lfoGain.gain.value = 160;
    lfo.connect(lfoGain).connect(filter.frequency);
    lfo.start();
    // detuned pair beats at ~0.22Hz — a slow breathing pulse
    const oscs = [
      ["sine", 108, 0.55],
      ["sine", 108.22, 0.55],
      ["sine", 162, 0.26],
      ["triangle", 216, 0.12],
    ].map(([type, f, vol]) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = type;
      o.frequency.value = f;
      g.gain.value = vol;
      o.connect(g).connect(filter);
      o.start();
      return o;
    });
    const notes = [432, 486, 540, 648, 810];
    let step = 0;
    const sparkle = setInterval(() => {
      if (this.muted || !this.ambient) return;
      step += 1;
      const t0 = this.ctx.currentTime;
      if (step % 2 === 0) {
        // gentle gliding sweep — energy flowing through
        const up = step % 4 === 0;
        const o = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        o.type = "sine";
        o.frequency.setValueAtTime(up ? 270 : 540, t0);
        o.frequency.exponentialRampToValueAtTime(up ? 540 : 270, t0 + 5);
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.012, t0 + 2.2);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 5.6);
        o.connect(g).connect(this.master);
        o.start(t0);
        o.stop(t0 + 5.9);
      } else if (Math.random() < 0.7) {
        const f = notes[Math.floor(Math.random() * notes.length)];
        const o = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        o.type = "sine";
        o.frequency.value = f;
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.015, t0 + 1);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 4);
        o.connect(g).connect(this.master);
        o.start(t0);
        o.stop(t0 + 4.2);
      }
    }, 5200);
    this.ambient = { bus, lfo, oscs, sparkle };
  }

  // RETRO — soft chip arpeggio only (no drone).
  _ambientRetro() {
    const t = this.ctx.currentTime;
    const bus = this.ctx.createGain();
    bus.gain.setValueAtTime(0.0001, t);
    bus.gain.exponentialRampToValueAtTime(0.03, t + 2);
    bus.connect(this.master);
    const pattern = [220, 277.18, 329.63, 440, 329.63, 277.18];
    let step = 0;
    const sparkle = setInterval(() => {
      if (this.muted || !this.ambient) return;
      const t0 = this.ctx.currentTime;
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = "triangle";
      o.frequency.value = pattern[step % pattern.length];
      step += 1;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.014, t0 + 0.04);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.5);
      o.connect(g).connect(this.master);
      o.start(t0);
      o.stop(t0 + 0.6);
    }, 640);
    this.ambient = { bus, lfo: null, oscs: [], sparkle };
  }

  _noiseLoop() {
    const len = this.ctx.sampleRate * 2;
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    return src;
  }

  // Shishi-odoshi PLONK — hollow bamboo strike, then water resettles.
  _plonk(t0) {
    const k = this.waterMix.plonk;
    if (k < 0.02) return;
    [[196, 0.12, 0.4], [392, 0.055, 0.28], [98, 0.05, 0.5]].forEach(([f, p, dur]) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = "sine";
      o.frequency.setValueAtTime(f * 1.12, t0);
      o.frequency.exponentialRampToValueAtTime(f, t0 + 0.05);
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(p * k, t0 + 0.012);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      o.connect(g).connect(this.master);
      o.start(t0);
      o.stop(t0 + dur + 0.1);
    });
  }

  // CLASSIC — close bamboo water trickle with droplet plinks and a periodic shishi-odoshi PLONK.
  _ambientClassic() {
    if (this.gardenAuto) Object.assign(this.waterMix, this._timeOfDayMix());
    const t = this.ctx.currentTime;
    const bus = this.ctx.createGain();
    bus.gain.setValueAtTime(0.0001, t);
    bus.gain.exponentialRampToValueAtTime(0.11, t + 2.5);
    bus.connect(this.master);

    // very subtle distant wash for depth
    const bed = this._noiseLoop();
    const bedF = this.ctx.createBiquadFilter();
    bedF.type = "lowpass";
    bedF.frequency.value = 400;
    const bedG = this.ctx.createGain();
    bedG.gain.value = 0.028;
    bed.connect(bedF).connect(bedG).connect(bus);
    bed.start();

    // continuous pour from the bamboo spout into the basin — fuller splashy body
    const pour = this._noiseLoop();
    const pourF = this.ctx.createBiquadFilter();
    pourF.type = "bandpass";
    pourF.frequency.value = 1400;
    pourF.Q.value = 1.1;
    const pourG = this.ctx.createGain();
    pourG.gain.value = 0.055 * this.waterMix.pour;
    pour.connect(pourF).connect(pourG).connect(bus);
    pour.start();
    const pourLfo = this.ctx.createOscillator();
    const pourLfoG = this.ctx.createGain();
    pourLfo.frequency.value = 0.5;
    pourLfoG.gain.value = 180;
    pourLfo.connect(pourLfoG).connect(pourF.frequency);
    pourLfo.start();

    // soft rain — wide, gentle patter (level driven by mix)
    const rain = this._noiseLoop();
    const rainF = this.ctx.createBiquadFilter();
    rainF.type = "bandpass";
    rainF.frequency.value = 1000;
    rainF.Q.value = 0.4;
    const rainG = this.ctx.createGain();
    rainG.gain.value = 0.09 * this.waterMix.rain;
    rain.connect(rainF).connect(rainG).connect(bus);
    rain.start();
    const rainLfo = this.ctx.createOscillator();
    const rainLfoG = this.ctx.createGain();
    rainLfo.frequency.value = 0.18;
    rainLfoG.gain.value = 220;
    rainLfo.connect(rainLfoG).connect(rainF.frequency);
    rainLfo.start();

    // close trickle — granular stream of random micro-droplets (no periodic modulation)
    const trickG = this.ctx.createGain();
    trickG.gain.value = this.waterMix.trickle;
    trickG.connect(bus);
    const grain = (t0) => {
      const src = this.ctx.createBufferSource();
      src.buffer = this._grainBuf();
      const bp = this.ctx.createBiquadFilter();
      bp.type = "bandpass";
      bp.frequency.value = 1600 + Math.random() * 2000;
      bp.Q.value = 7 + Math.random() * 5;
      const g = this.ctx.createGain();
      const dur = 0.02 + Math.random() * 0.035;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.07 + Math.random() * 0.1, t0 + 0.006);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      src.connect(bp).connect(g).connect(trickG);
      src.start(t0, Math.random() * 0.8, dur + 0.05);
      if (Math.random() < 0.18) {
        const o = this.ctx.createOscillator();
        const og = this.ctx.createGain();
        o.type = "sine";
        const f = 700 + Math.random() * 800;
        o.frequency.setValueAtTime(f, t0);
        o.frequency.exponentialRampToValueAtTime(f * 1.3, t0 + 0.03);
        og.gain.setValueAtTime(0.0001, t0);
        og.gain.exponentialRampToValueAtTime(0.018 + Math.random() * 0.018, t0 + 0.005);
        og.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.05);
        o.connect(og).connect(trickG);
        o.start(t0);
        o.stop(t0 + 0.08);
      }
    };
    const trickleTimer = setInterval(() => {
      if (this.muted || !this.ambient || this.waterMix.trickle < 0.02) return;
      const t0 = this.ctx.currentTime;
      const n = 1 + (Math.random() < 0.5 ? 1 : 0) + (Math.random() < 0.2 ? 1 : 0);
      for (let i = 0; i < n; i += 1) grain(t0 + Math.random() * 0.06);
    }, 65);

    // droplets + predictable PLONK (~9-12s apart)
    let ticks = 0;
    let nextPlonk = 28 + Math.floor(Math.random() * 12);
    const sparkle = setInterval(() => {
      if (this.muted || !this.ambient) return;
      ticks += 1;
      const t0 = this.ctx.currentTime;
      if (ticks >= nextPlonk) {
        ticks = 0;
        nextPlonk = 28 + Math.floor(Math.random() * 12);
        this._plonk(t0);
      } else if (this.waterMix.birds >= 0.02 && Math.random() < 0.035) {
        this._birdCall(t0);
      } else if (this.waterMix.crickets > 0.02 && Math.random() < 0.45) {
        this._cricketTrill(t0, this.waterMix.crickets);
      } else if (Math.random() < 0.4 && this.waterMix.trickle > 0.02) {
        // watery droplet bloop — low, rising pitch so it never reads as birdsong
        const f = 520 + Math.random() * 700;
        const o = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        o.type = "sine";
        o.frequency.setValueAtTime(f, t0);
        o.frequency.exponentialRampToValueAtTime(f * 1.45, t0 + 0.05);
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime((0.014 + Math.random() * 0.016) * this.waterMix.trickle, t0 + 0.008);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.08);
        o.connect(g).connect(this.master);
        o.start(t0);
        o.stop(t0 + 0.11);
      }
    }, 300);
    this.ambient = {
      bus,
      lfo: null,
      oscs: [bed, pour, rain, pourLfo, rainLfo],
      sparkle,
      trickleTimer,
      water: { pourG, trickG, rainG },
    };
  }

  _grainBuf() {
    if (this._gbuf) return this._gbuf;
    const len = this.ctx.sampleRate;
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i += 1) d[i] = Math.random() * 2 - 1;
    this._gbuf = buf;
    return this._gbuf;
  }

  stopAmbient() {
    if (!this.ambient || !this.ctx) return;
    const { bus, lfo, oscs, sparkle, trickleTimer } = this.ambient;
    this.ambient = null;
    clearInterval(sparkle);
    if (trickleTimer) clearInterval(trickleTimer);
    bus.gain.setTargetAtTime(0.0001, this.ctx.currentTime, 0.3);
    setTimeout(() => {
      oscs.forEach((o) => o.stop());
      if (lfo) lfo.stop();
      bus.disconnect();
    }, 1500);
  }
}

export const sfx = new SoundEngine();
if (typeof window !== "undefined") window.__sfx = sfx;
